import { PencilSquareIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback } from 'react';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';
import { DateValue, useFormatDate } from '../../hooks/useFormatDate';
import { dayAfterTermEnd } from '../../utils/dates';
import { T, useT } from '../../translation/src';
import { Tooltip } from '../Tooltip';
import { MonthlyAllowance, SubscriptionType } from '../../types/graphqlTypes';

export interface SubscriptionHeaderProps {
  type: SubscriptionType;
  // A plan change waiting for scheduledAt (a downgrade at the end of the term, an upgrade on its day).
  scheduledType?: SubscriptionType | null;
  scheduledAt?: DateValue | null;
  // In the future for a term sold ahead of time.
  startDate?: DateValue | null;
  endDate?: DateValue | null;
  // Paused in Odoo since then (a customer that did not pay): blocked, while the term runs on.
  pausedAt?: DateValue | null;
  monthlyAllowance?: Pick<MonthlyAllowance, 'total' | 'remaining' | 'renewsAt'> | null;
  // For whoever may change the plan (the superadmin): the card itself opens the plan change, and shows it can.
  onChangePlan?: () => void;
  // Why the plan cannot be changed right now: the card is then greyed out and says so on hover.
  changePlanDisabledReason?: React.ReactNode;
}

// Top block of the subscription page and the superadmin credit modal. An allowance grant expires exactly when the next
// period starts, so the renewal date belongs here once — never as a per-row expiry.
const SubscriptionHeader_: React.FC<SubscriptionHeaderProps> = ({ type, scheduledType, scheduledAt, startDate, endDate, pausedAt, monthlyAllowance, onChangePlan: propsOnChangePlan, changePlanDisabledReason }) => {
  const onChangePlan = changePlanDisabledReason ? undefined : propsOnChangePlan;
  const changePlanLabel = useT('change plan', { swc: true });
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onChangePlan?.();
    },
    [onChangePlan],
  );
  const planTranslations = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const paused = !!pausedAt;
  const expired = !!endDate && new Date(endDate) <= new Date();
  const pending = !expired && !!startDate && new Date(startDate) > new Date();
  // A downgrade's moment is the last instant of the term: the day shown is the one after.
  const changeAt = scheduledAt ?? endDate;
  const changing = !expired && !!scheduledType && !!changeAt;
  // The final period ends with the subscription itself — nothing renews after it.
  const renews = !paused && !!monthlyAllowance && (!endDate || new Date(monthlyAllowance.renewsAt) < new Date(endDate));

  const card = (
    <div
      {...(onChangePlan && { role: 'button', tabIndex: 0, onClick: onChangePlan, onKeyDown, title: changePlanLabel, 'aria-label': changePlanLabel })}
      aria-disabled={changePlanDisabledReason ? true : undefined}
      className={classNames(
        'group flex flex-col gap-1 rounded-md border p-3',
        expired ? 'border-error-red-500 bg-error-red-200/30' : paused || changing || pending ? 'border-warning-yellow-500 bg-warning-yellow-200/30 dark:bg-transparent' : 'border-neon-green-500 bg-neon-green-50/50 dark:bg-transparent',
        onChangePlan && 'cursor-pointer transition-colors hover:bg-gray-100/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-french-violet-600 dark:hover:bg-white/5',
        !!changePlanDisabledReason && !!propsOnChangePlan && 'cursor-not-allowed opacity-60',
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide">
          {planTranslations[type]} <T _str="subscription" />
          {/* That the card can be clicked, where it can. */}
          {propsOnChangePlan && <PencilSquareIcon className="ml-1.5 inline h-3.5 w-3.5 -translate-y-px text-gray-400 transition-colors group-hover:text-current" />}
          {expired && (
            <span className="ml-2 rounded-full bg-error-red-500 px-2 py-0.5 text-xs font-medium normal-case tracking-normal text-white">
              <T _str="expired" swc />
            </span>
          )}
          {paused && (
            <span className="ml-2 rounded-full bg-warning-yellow-500 px-2 py-0.5 text-xs font-medium normal-case tracking-normal text-black">
              <T _str="paused since {date}" args={{ date: formatDay(pausedAt) }} swc />
            </span>
          )}
          {pending && (
            <span className="ml-2 rounded-full bg-warning-yellow-500 px-2 py-0.5 text-xs font-medium normal-case tracking-normal text-black">
              <T _str="starts {date}" args={{ date: formatDay(startDate) }} swc />
            </span>
          )}
          {changing && (
            <span className="ml-2 rounded-full bg-warning-yellow-500 px-2 py-0.5 text-xs font-medium normal-case tracking-normal text-black">
              <T _str="change scheduled" swc />
            </span>
          )}
        </span>
        {endDate && (
          <span className={classNames('text-xs shrink-0', expired ? 'font-medium text-error-red-500' : 'text-gray-500')}>
            {expired ? <T _str="ended" /> : <T _str="ends" />} {formatDay(endDate)}
          </span>
        )}
      </div>

      {monthlyAllowance && (
        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-gray-700 dark:text-gray-300">
            <T _str="monthly projects" swc />
          </span>
          <span className="text-xs text-gray-500 shrink-0">
            <T _str="{remaining} of {total} left" args={{ remaining: monthlyAllowance.remaining, total: monthlyAllowance.total }} />
            {renews && (
              <>
                {' · '}
                <T _str="renews" /> {formatDay(monthlyAllowance.renewsAt)}
              </>
            )}
          </span>
        </div>
      )}

      {monthlyAllowance && (
        <span className="text-xs text-gray-500">
          <T _str="Unused monthly projects don't carry over." />
        </span>
      )}
      {type === SubscriptionType.Flex && (
        <span className="text-xs text-gray-500">
          <T _str="Projects are bought as you need them." />
        </span>
      )}
      {expired && (
        <span className="text-xs text-error-red-500">
          <T _str="This subscription has ended. Monthly projects are no longer granted." />
        </span>
      )}
      {paused && (
        <span className="text-xs font-medium text-warning-yellow-700 dark:text-warning-yellow-500">
          <T _str="This subscription is paused. Uploads wait until it resumes. The term runs on as usual." />
        </span>
      )}
      {pending && (
        <span className="text-xs font-medium text-warning-yellow-700 dark:text-warning-yellow-500">
          <T _str="This subscription starts on {date}. Until then projects are bought as you need them." args={{ date: formatDay(startDate) }} />
        </span>
      )}
      {changing && (
        <span className="text-xs font-medium text-warning-yellow-700 dark:text-warning-yellow-500">
          {scheduledType === SubscriptionType.Flex ? (
            <T _str="This subscription ends on {date}. Until then nothing changes." args={{ date: formatDay(dayAfterTermEnd(changeAt)) }} />
          ) : (
            <T _str="This subscription becomes {plan} on {date}. Until then nothing changes." args={{ plan: planTranslations[scheduledType], date: formatDay(dayAfterTermEnd(changeAt)) }} />
          )}
        </span>
      )}
    </div>
  );
  if (!changePlanDisabledReason || !propsOnChangePlan) return card;
  return (
    <Tooltip message={<span>{changePlanDisabledReason}</span>} delay={0}>
      {card}
    </Tooltip>
  );
};

export const SubscriptionHeader = memo(SubscriptionHeader_);
